import { useState } from "react";
import { formatCurrency } from "../../../utils/formatters";
import { useRequestPaymentEditMutation, useGetLedgerAccountsQuery } from "../api/billingApi";

interface Props {
  payment: any;
  onClose: () => void;
  onSuccess: () => void;
}

const PAYMENT_METHODS = ["bank_transfer", "cash", "pos", "online"];

// What the row calls the account, whichever field the API populated.
function accountLabel(a: any) {
  const name = a?.account_name || a?.name || "Account";
  return [name, a?.bank_name, a?.account_number].filter(Boolean).join(" · ");
}

export default function RequestPaymentEditModal({ payment, onClose, onSuccess }: Props) {
  const amountInNaira = payment.amount ? payment.amount / 100 : 0;
  const currentBankId = payment.bank_account_id ? String(payment.bank_account_id) : "";

  const [amount, setAmount] = useState(String(amountInNaira));
  const [paymentMethod, setPaymentMethod] = useState(payment.payment_method || "");
  const [paidAt, setPaidAt] = useState(
    payment.paid_at ? payment.paid_at.slice(0, 10) : ""
  );
  const [reference, setReference] = useState(payment.reference || "");
  const [bankAccountId, setBankAccountId] = useState(currentBankId);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  const { data: bankAccounts = [] } = useGetLedgerAccountsQuery();
  const [requestEdit, { isLoading }] = useRequestPaymentEditMutation();

  // The listing doesn't carry the account name, so resolve it from the same
  // list the picker uses rather than showing a bare id as the current value.
  const currentAccount = bankAccounts.find(
    (a: any) => String(a.id) === currentBankId
  );

  const hasChanges =
    Number(amount) !== amountInNaira ||
    paymentMethod !== (payment.payment_method || "") ||
    paidAt !== (payment.paid_at ? payment.paid_at.slice(0, 10) : "") ||
    reference !== (payment.reference || "") ||
    bankAccountId !== currentBankId;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError("Reason for edit is required.");
      return;
    }
    if (!hasChanges) {
      setError("No changes detected. Modify at least one field.");
      return;
    }
    setError("");
    try {
      await requestEdit({
        id: payment.id,
        body: {
          reason: reason.trim(),
          amount: Math.round(Number(amount) * 100),
          payment_method: paymentMethod,
          paid_at: paidAt || undefined,
          reference: reference || undefined,
          bank_account_id: bankAccountId ? Number(bankAccountId) : undefined,
          notes: notes.trim() || undefined,
        },
      }).unwrap();
      onSuccess();
    } catch (err: any) {
      setError(err?.data?.message || "Failed to submit edit request. Try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-black text-slate-900 uppercase tracking-widest">
              Request Payment Edit
            </h2>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">
              Receipt #{payment.receipt_number || payment.reference || payment.id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors text-xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Current vs Proposed side-by-side labels */}
          <div className="grid grid-cols-2 gap-3 text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">
            <span>Current Value</span>
            <span>Proposed Value</span>
          </div>

          {/* Amount */}
          <div className="grid grid-cols-2 gap-3 items-center">
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Amount</p>
              <p className="text-sm font-black text-slate-700">{formatCurrency(payment.amount)}</p>
            </div>
            <div>
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                New Amount (₦)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm font-bold text-slate-800 bg-slate-50 outline-none focus:ring-2 ring-orange-100"
              />
            </div>
          </div>

          {/* Payment Method */}
          <div className="grid grid-cols-2 gap-3 items-center">
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Method</p>
              <p className="text-sm font-black text-slate-700 capitalize">{payment.payment_method || "—"}</p>
            </div>
            <div>
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                New Method
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm font-bold text-slate-800 bg-slate-50 outline-none"
              >
                <option value="">Select method</option>
                {PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m.replace("_", " ").toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Date Paid */}
          <div className="grid grid-cols-2 gap-3 items-center">
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Date Paid</p>
              <p className="text-sm font-black text-slate-700">
                {payment.paid_at
                  ? new Date(payment.paid_at).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" })
                  : "—"}
              </p>
            </div>
            <div>
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                New Date
              </label>
              <input
                type="date"
                value={paidAt}
                onChange={(e) => setPaidAt(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm font-bold text-slate-800 bg-slate-50 outline-none focus:ring-2 ring-orange-100"
              />
            </div>
          </div>

          {/* Reference */}
          <div className="grid grid-cols-2 gap-3 items-center">
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Reference</p>
              <p className="text-sm font-mono text-slate-700 truncate">{payment.reference || "—"}</p>
            </div>
            <div>
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                New Reference
              </label>
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm font-mono text-slate-800 bg-slate-50 outline-none focus:ring-2 ring-orange-100"
              />
            </div>
          </div>

          {/* Bank account paid into */}
          <div className="grid grid-cols-2 gap-3 items-center">
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Paid Into</p>
              <p className="text-sm font-black text-slate-700 truncate">
                {currentAccount ? accountLabel(currentAccount) : payment.bank?.name || "—"}
              </p>
            </div>
            <div>
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                New Bank Account
              </label>
              <select
                value={bankAccountId}
                onChange={(e) => setBankAccountId(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm font-bold text-slate-800 bg-slate-50 outline-none focus:ring-2 ring-orange-100"
              >
                <option value="">Select account</option>
                {bankAccounts.map((a: any) => (
                  <option key={a.id} value={String(a.id)}>
                    {accountLabel(a)}
                  </option>
                ))}
              </select>
              {bankAccountId !== currentBankId && bankAccountId && (
                <p className="text-[9px] font-bold text-orange-600 mt-1 leading-tight">
                  Approving this also moves the ledger credit to this account.
                </p>
              )}
            </div>
          </div>

          {/* Reason (required) */}
          <div>
            <label className="text-[10px] font-black text-slate-700 uppercase tracking-widest block mb-1">
              Reason for Edit <span className="text-rose-500">*</span>
            </label>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why this payment needs to be edited..."
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 ring-orange-100 resize-none"
            />
          </div>

          {/* Notes (optional) */}
          <div>
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
              Additional Notes (optional)
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any extra context for the approving admin..."
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 ring-orange-100"
            />
          </div>

          {error && (
            <p className="text-xs font-bold text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2">
              {error}
            </p>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl bg-orange-500 text-white hover:bg-orange-600 transition-all disabled:opacity-50"
            >
              {isLoading ? "Submitting..." : "Submit Edit Request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
